import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Banknote,
  Check,
  ChevronRight,
  CircleDollarSign,
  Download,
  FileClock,
  FileSpreadsheet,
  FileText,
  History,
  Link2,
  LoaderCircle,
  RefreshCw,
  Search,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  beginPaymentScope,
  commitPaymentScope,
  EMPTY_PAYMENT_SCOPED_PANELS,
  isPaymentScopedTab,
  resolvePaymentScope,
  selectPaymentExceptions,
  type PaymentScopedPanels,
} from "../../lib/arRecoveryPaymentScope";
import {
  confirmPaymentMatch,
  createPayment,
  createPaymentAllocation,
  createPaymentException,
  createPaymentClarification,
  decidePaymentCorrection,
  decidePaymentUploadRow,
  getPayment,
  getPaymentApprovalResolution,
  getPaymentDocumentUrl,
  getPaymentReports,
  getPaymentUpload,
  listClientAllocationInstructions,
  listPaymentCorrections,
  listPaymentExceptions,
  listPaymentClarifications,
  listPaymentPromiseLinks,
  linkPaymentPromise,
  listPaymentUploads,
  listPayments,
  reversePaymentAllocation,
  requestPaymentCorrection,
  savePaymentApprovalPolicy,
  transitionPayment,
  updatePaymentException,
  uploadPaymentDocument,
  uploadPaymentFile,
  type CanonicalPayment,
  type PaymentClientAllocationInstruction,
  type PaymentCorrection,
  type PaymentDetail,
  type PaymentException,
  type PaymentApprovalResolution,
  type PaymentClarification,
  type PaymentMetrics,
  type PaymentPermissions,
  type PaymentUpload,
  type PaymentUploadRow,
  type PaymentPromiseLink,
} from "../../lib/arRecoveryPaymentsApi";

type PaymentCenterTab = "summary" | "matching" | "allocation" | "verification" | "operations" | "exceptions" | "documents" | "history" | "reporting";

const EMPTY_METRICS: PaymentMetrics = {
  totalCount: 0,
  totalAmountCents: 0,
  pendingReview: 0,
  unmatched: 0,
  partiallyMatched: 0,
  fullyAllocated: 0,
  pendingVerification: 0,
  verified: 0,
  possibleDuplicates: 0,
  rejected: 0,
  reversed: 0,
  exceptions: 0,
};

const EMPTY_PERMISSIONS: PaymentPermissions = {
  viewQueue: false,
  createManual: false,
  submitClient: false,
  uploadFile: false,
  reviewUpload: false,
  edit: false,
  match: false,
  allocate: false,
  overrideAllocation: false,
  verify: false,
  approve: false,
  reject: false,
  reverse: false,
  reviewDuplicate: false,
  overrideDuplicate: false,
  manageExceptions: false,
  viewDocuments: false,
  uploadDocuments: false,
  viewHistory: false,
  viewAudit: false,
  exportReport: false,
};

const tabs: Array<{ key: PaymentCenterTab; label: string; icon: typeof Banknote }> = [
  { key: "summary", label: "Summary", icon: Banknote },
  { key: "matching", label: "Matching", icon: Link2 },
  { key: "allocation", label: "Allocation", icon: CircleDollarSign },
  { key: "verification", label: "Verification", icon: ShieldCheck },
  { key: "operations", label: "Policy & Links", icon: FileClock },
  { key: "exceptions", label: "Exceptions", icon: AlertTriangle },
  { key: "documents", label: "Documents", icon: FileText },
  { key: "history", label: "History & Audit", icon: History },
  { key: "reporting", label: "Reporting", icon: FileSpreadsheet },
];

export function ARRecoveryPaymentCenter() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [payments, setPayments] = useState<CanonicalPayment[]>([]);
  const [metrics, setMetrics] = useState(EMPTY_METRICS);
  const [permissions, setPermissions] = useState(EMPTY_PERMISSIONS);
  const [manageApprovalPolicy, setManageApprovalPolicy] = useState(false);
  const [actorUserId, setActorUserId] = useState("");
  const [selected, setSelected] = useState<PaymentDetail | null>(null);
  const [search, setSearch] = useState(searchParams.get("search") || "");
  const [status, setStatus] = useState(searchParams.get("status") || "");
  const [method, setMethod] = useState("");
  const [sort, setSort] = useState("payment-date-desc");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [pageCount, setPageCount] = useState(1);
  const [tab, setTab] = useState<PaymentCenterTab>("summary");
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [isManualOpen, setIsManualOpen] = useState(false);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [uploads, setUploads] = useState<PaymentUpload[]>([]);
  const [activeUpload, setActiveUpload] = useState<{ upload: PaymentUpload; rows: PaymentUploadRow[] } | null>(null);
  const [reports, setReports] = useState<Awaited<ReturnType<typeof getPaymentReports>> | null>(null);
  const [approvalResolution, setApprovalResolution] = useState<PaymentApprovalResolution | null>(null);
  const [promiseLinks, setPromiseLinks] = useState<PaymentPromiseLink[]>([]);
  const [clarifications, setClarifications] = useState<PaymentClarification[]>([]);
  const [panels, setPanels] = useState<PaymentScopedPanels>(EMPTY_PAYMENT_SCOPED_PANELS);
  const [documentError, setDocumentError] = useState("");
  const scopedPaymentIdRef = useRef("");

  async function refreshQueue(preferredPaymentId?: string) {
    setLoading(true);
    setError("");
    try {
      const result = await listPayments({
        search: search.trim(),
        operationalStatus: status,
        paymentMethod: method,
        accountId: searchParams.get("accountId") || undefined,
        sort,
        page,
        pageSize,
      });
      setPayments(result.payments);
      setMetrics(result.metrics);
      setPermissions(result.permissions);
      setManageApprovalPolicy(result.operationalPermissions.manageApprovalPolicy);
      setActorUserId(result.actorUserId);
      setPageCount(Math.max(1, result.pagination.pageCount));
      const requestedId = preferredPaymentId || searchParams.get("paymentId");
      if (requestedId) await selectPayment(requestedId, false);
      else if (selected && !result.payments.some((payment) => payment.paymentId === selected.payment.paymentId)) setSelected(null);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Payment queue could not be loaded.");
    } finally {
      setLoading(false);
    }
  }

  function commitScopedPanels(paymentId: string, patch: Partial<Omit<PaymentScopedPanels, "paymentId">>) {
    if (scopedPaymentIdRef.current !== paymentId) return;
    setPanels((current) => commitPaymentScope(current, paymentId, patch));
  }

  async function loadScopedPanel(paymentId: string, targetTab: PaymentCenterTab) {
    if (!isPaymentScopedTab(targetTab)) return;
    try {
      if (targetTab === "operations") {
        const result = await listPaymentCorrections(paymentId);
        commitScopedPanels(paymentId, { corrections: result.corrections });
      } else if (targetTab === "exceptions") {
        const result = await listPaymentExceptions();
        commitScopedPanels(paymentId, { exceptions: selectPaymentExceptions(result.exceptions, paymentId) });
      } else {
        const result = await listClientAllocationInstructions(paymentId);
        commitScopedPanels(paymentId, { clientAllocations: result.instructions });
      }
    } catch {
      commitScopedPanels(paymentId, {});
    }
  }

  async function selectPayment(paymentId: string, updateUrl = true) {
    setError("");
    // Stale Payment-scoped panels are dropped before the new detail is fetched so a prior
    // Payment's corrections or exceptions can never render under a different Payment.
    scopedPaymentIdRef.current = paymentId;
    setPanels(beginPaymentScope(paymentId));
    try {
      const detail = await getPayment(paymentId);
      setSelected(detail);
      setPermissions(detail.permissions);
      const [approval, promiseResult, clarificationResult] = await Promise.all([
        getPaymentApprovalResolution(paymentId).catch(() => null),
        listPaymentPromiseLinks(paymentId).catch(() => ({ promiseLinks: [] })),
        listPaymentClarifications(paymentId).catch(() => ({ clarifications: [] })),
      ]);
      setApprovalResolution(approval);
      setPromiseLinks(promiseResult.promiseLinks);
      setClarifications(clarificationResult.clarifications);
      await loadScopedPanel(paymentId, tab);
      if (updateUrl) {
        const next = new URLSearchParams(searchParams);
        next.set("paymentId", paymentId);
        setSearchParams(next, { replace: true });
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "Payment detail could not be loaded.");
    }
  }

  async function runMutation(action: () => Promise<unknown>, success: string) {
    setWorking(true);
    setError("");
    setMessage("");
    try {
      await action();
      setMessage(success);
      await refreshQueue(selected?.payment.paymentId);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The payment action could not be completed.");
    } finally {
      setWorking(false);
    }
  }

  useEffect(() => {
    void refreshQueue();
    // Queue refresh is intentionally driven by committed filter controls.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, status, method, sort]);

  async function loadOperationalData(targetTab: PaymentCenterTab) {
    setTab(targetTab);
    if (targetTab === "reporting") {
      setReports(await getPaymentReports());
      return;
    }
    const activePaymentId = selected?.payment.paymentId;
    if (!activePaymentId) return;
    if (targetTab === "operations") {
      const [approval, promiseResult, clarificationResult] = await Promise.all([
        getPaymentApprovalResolution(activePaymentId).catch(() => null),
        listPaymentPromiseLinks(activePaymentId).catch(() => ({ promiseLinks: [] })),
        listPaymentClarifications(activePaymentId).catch(() => ({ clarifications: [] })),
      ]);
      setApprovalResolution(approval);
      setPromiseLinks(promiseResult.promiseLinks);
      setClarifications(clarificationResult.clarifications);
    }
    await loadScopedPanel(activePaymentId, targetTab);
  }

  const returnRoute = searchParams.get("return");
  const selectedPayment = selected?.payment;
  const scoped = resolvePaymentScope(panels, selectedPayment?.paymentId);

  return (
    <section className="payment-center-enterprise mx-auto grid w-full max-w-[100rem] gap-4" aria-label="Payment Center">
      <header className="overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_18px_44px_rgba(10,37,64,.08)]">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-[#BFEAFF] px-5 py-4">
          <div className="min-w-0">
            <p className="text-[11px] font-black uppercase tracking-[.18em] text-[#14B8A6]">Canonical Payment Operations</p>
            <h2 className="mt-1 font-display text-3xl font-bold tracking-[-.03em] text-[#0A2540]">Payment Center</h2>
            <p className="mt-1 max-w-3xl text-sm font-semibold leading-6 text-[#475569]">
              Record, verify, govern, and track allocation context for client-reported customer payments without calculating fees, settlements, or ERP cash application.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {returnRoute === "account-recovery" ? (
              <Link className="payment-button payment-button-secondary" to="/admin/ar-recovery/customer-accounts">
                <ArrowLeft className="size-4" aria-hidden="true" />
                Return to Account Recovery
              </Link>
            ) : null}
            <button className="payment-button payment-button-secondary" type="button" onClick={() => setIsUploadOpen(true)} disabled={!permissions.uploadFile}>
              <Upload className="size-4" aria-hidden="true" />
              Import Payments
            </button>
            <button className="payment-button payment-button-primary" type="button" onClick={() => setIsManualOpen(true)} disabled={!permissions.createManual}>
              <CircleDollarSign className="size-4" aria-hidden="true" />
              Record Payment
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-px bg-[#BFEAFF] sm:grid-cols-3 xl:grid-cols-6">
          <PaymentMetric label="Payments" value={metrics.totalCount.toLocaleString()} note={money(metrics.totalAmountCents)} />
          <PaymentMetric label="Pending Review" value={metrics.pendingReview.toLocaleString()} note="Needs operator review" />
          <PaymentMetric label="Unmatched" value={metrics.unmatched.toLocaleString()} note={`${metrics.partiallyMatched} partial`} />
          <PaymentMetric label="Allocated" value={metrics.fullyAllocated.toLocaleString()} note="Fully allocated in Payment Center" />
          <PaymentMetric label="Duplicates" value={metrics.possibleDuplicates.toLocaleString()} note="Awaiting decision" />
          <PaymentMetric label="Exceptions" value={metrics.exceptions.toLocaleString()} note="Open items" />
        </div>
      </header>

      {message ? <PaymentNotice tone="success" onClose={() => setMessage("")}>{message}</PaymentNotice> : null}
      {error ? <PaymentNotice tone="error" onClose={() => setError("")}>{error}</PaymentNotice> : null}

      <div className="grid min-w-0 gap-4 xl:grid-cols-[minmax(24rem,38%)_minmax(0,62%)]">
        <article className="min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_16px_38px_rgba(10,37,64,.07)]">
          <div className="border-b border-[#BFEAFF] p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Operational Queue</p>
                <h3 className="mt-1 text-lg font-black text-[#0A2540]">Reported payments</h3>
              </div>
              <button type="button" className="payment-icon-button" aria-label="Refresh payment queue" onClick={() => void refreshQueue()} disabled={loading}>
                <RefreshCw className={`size-4 ${loading ? "animate-spin" : ""}`} aria-hidden="true" />
              </button>
            </div>
            <form
              className="mt-3 grid gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                setPage(1);
                void refreshQueue();
              }}
            >
              <label className="relative block">
                <span className="sr-only">Search payments</span>
                <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-[#14B8A6]" aria-hidden="true" />
                <input className="payment-input pl-10" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search payment, customer, account, or reference" />
              </label>
              <div className="grid grid-cols-2 gap-2">
                <select className="payment-input" aria-label="Payment status" value={status} onChange={(event) => { setPage(1); setStatus(event.target.value); }}>
                  <option value="">All statuses</option>
                  <option value="REPORTED">Reported</option>
                  <option value="UNDER_REVIEW">In review</option>
                  <option value="VERIFIED">Verified</option>
                  <option value="APPROVED">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="REVERSED">Reversed</option>
                </select>
                <select className="payment-input" aria-label="Payment method" value={method} onChange={(event) => { setPage(1); setMethod(event.target.value); }}>
                  <option value="">All methods</option>
                  <option>ACH</option>
                  <option>Check</option>
                  <option>Wire</option>
                  <option>Card</option>
                  <option>Cash</option>
                  <option>Other</option>
                </select>
              </div>
              <select className="payment-input" aria-label="Sort payments" value={sort} onChange={(event) => { setPage(1); setSort(event.target.value); }}>
                <option value="payment-date-desc">Newest payment date</option>
                <option value="payment-date-asc">Oldest payment date</option>
                <option value="amount-desc">Highest amount</option>
                <option value="amount-asc">Lowest amount</option>
                <option value="customer-asc">Customer name</option>
              </select>
            </form>
          </div>

          <div className="grid min-h-[24rem] content-start gap-2 bg-[#F4FBFF] p-3">
            {loading ? <PaymentLoading label="Loading canonical payments" /> : null}
            {!loading && payments.length === 0 ? (
              <PaymentEmpty icon={Banknote} title="No payments found" detail="Adjust the queue filters or record a new client-reported payment." />
            ) : null}
            {payments.map((payment) => (
              <button
                key={payment.paymentId}
                type="button"
                className={`grid min-w-0 gap-2 rounded-2xl border p-3 text-left transition focus-ring ${
                  selectedPayment?.paymentId === payment.paymentId
                    ? "border-[#14B8A6] bg-white shadow-[0_10px_24px_rgba(20,184,166,.14)]"
                    : "border-[#BFEAFF] bg-white hover:border-[#14B8A6]"
                }`}
                onClick={() => void selectPayment(payment.paymentId)}
              >
                <div className="flex min-w-0 items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-black text-[#0A2540]">{payment.customerName || "Unmatched customer"}</p>
                    <p className="mt-0.5 truncate text-xs font-bold text-[#475569]">{payment.accountNumber || "Account not matched"} · {payment.paymentMethod}</p>
                  </div>
                  <p className="shrink-0 text-base font-black text-[#0A2540]">{money(payment.amountCents)}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5">
                  <PaymentStatus value={payment.operationalStatus} />
                  <PaymentStatus value={payment.allocationStatus} />
                  {payment.duplicateStatus !== "CLEAR" ? <PaymentStatus value={payment.duplicateStatus} warning /> : null}
                </div>
                <div className="flex items-center justify-between gap-3 text-[11px] font-bold text-[#64748B]">
                  <span>{formatDate(payment.paymentDate)}</span>
                  <span className="truncate">{payment.externalPaymentReference || payment.clientPaymentReference || payment.paymentId}</span>
                </div>
              </button>
            ))}
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-[#BFEAFF] px-4 py-3 text-xs font-bold text-[#475569]">
            <button className="payment-page-button" type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)} aria-label="Previous payment page">
              <ArrowLeft className="size-4" aria-hidden="true" />
            </button>
            <div className="flex min-w-0 items-center gap-2">
              <span className="whitespace-nowrap">Page {page} of {pageCount}</span>
              <label className="flex items-center gap-1.5 whitespace-nowrap">
                <span>Rows</span>
                <select
                  className="rounded-lg border border-[#BFEAFF] bg-white px-2 py-1 text-xs font-bold text-[#0A2540] focus-ring"
                  aria-label="Payments per page"
                  value={pageSize}
                  onChange={(event) => {
                    setPage(1);
                    setPageSize(Number(event.target.value));
                  }}
                >
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
            <button className="payment-page-button" type="button" disabled={page >= pageCount} onClick={() => setPage((value) => value + 1)} aria-label="Next payment page">
              <ArrowRight className="size-4" aria-hidden="true" />
            </button>
          </div>
        </article>

        <article className="min-w-0 overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_16px_38px_rgba(10,37,64,.07)]">
          {!selected ? (
            <div className="grid min-h-[42rem] place-items-center p-8">
              <PaymentEmpty icon={ChevronRight} title="Select a payment" detail="Choose one queue record to review its canonical context, allocation, verification, documents, and history." />
            </div>
          ) : (
            <>
              <PaymentCommandHeader payment={selected.payment} />
              <nav className="flex max-w-full gap-1 overflow-x-auto border-b border-[#BFEAFF] bg-[#F4FBFF] px-3 py-2" aria-label="Payment workspace tabs">
                {tabs.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.key}
                      type="button"
                      className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-xl px-3 text-xs font-black transition focus-ring ${
                        tab === item.key ? "bg-[#0A2540] text-white" : "text-[#0A2540] hover:bg-white"
                      }`}
                      onClick={() => void loadOperationalData(item.key)}
                    >
                      <Icon className="size-4" aria-hidden="true" />
                      {item.label}
                    </button>
                  );
                })}
              </nav>
              <div className="min-w-0 p-4 sm:p-5">
                {tab === "summary" ? <PaymentSummary detail={selected} /> : null}
                {tab === "matching" ? (
                  <PaymentMatching
                    detail={selected}
                    disabled={!permissions.match || working}
                    onConfirm={(suggestion) => runMutation(
                      () => confirmPaymentMatch(selected.payment.paymentId, {
                        accountId: suggestion.entityType === "ACCOUNT" ? suggestion.entityId : undefined,
                        accountNumber: suggestion.entityType === "ACCOUNT" ? suggestion.entityLabel : undefined,
                        reason: "Operator accepted canonical match suggestion.",
                      }),
                      "Payment match confirmed.",
                    )}
                  />
                ) : null}
                {tab === "allocation" ? (
                  <PaymentAllocationPanel
                    detail={selected}
                    clientAllocations={scoped.clientAllocations}
                    clarifications={clarifications}
                    disabled={!permissions.allocate || working}
                    onAllocate={(input) => runMutation(
                      () => createPaymentAllocation(selected.payment.paymentId, input),
                      "Payment allocation recorded.",
                    )}
                    onReverse={(allocationId, reason) => runMutation(
                      () => reversePaymentAllocation(selected.payment.paymentId, allocationId, reason),
                      "Allocation reversed.",
                    )}
                  />
                ) : null}
                {tab === "verification" ? (
                  <PaymentVerification
                    detail={selected}
                    permissions={permissions}
                    disabled={working}
                    onAction={(action, reason) => runMutation(
                      () => transitionPayment(selected.payment.paymentId, action, reason),
                      `Payment ${action.replaceAll("_", " ")} completed.`,
                    )}
                  />
                ) : null}
                {tab === "operations" ? (
                  <PaymentOperationalControls
                    payment={selected.payment}
                    approval={approvalResolution}
                    promiseLinks={promiseLinks}
                    clarifications={clarifications}
                    corrections={scoped.corrections}
                    permissions={permissions}
                    manageApprovalPolicy={manageApprovalPolicy}
                    actorUserId={actorUserId}
                    disabled={working}
                    onSelectPayment={(paymentId) => void selectPayment(paymentId)}
                    onCorrection={(correctionType, reason) => runMutation(
                      () => requestPaymentCorrection(selected.payment.paymentId, { correctionType, requestedChanges: {}, reason }),
                      "Payment correction request created.",
                    )}
                    onCorrectionDecision={(correctionId, decision, reason) => runMutation(
                      () => decidePaymentCorrection(correctionId, { decision, reason }),
                      decision === "APPROVE" ? "Correction approved." : "Correction rejected.",
                    )}
                    onPromiseLink={(promiseId, promisedAmountCents, promiseDueDate) => runMutation(
                      () => linkPaymentPromise(selected.payment.paymentId, {
                        promiseId,
                        promiseReference: promiseId,
                        promisedAmountCents,
                        promiseDueDate,
                        confidence: "EXACT",
                        matchingFactors: ["Operator-confirmed Promise reference", "Selected payment account"],
                      }),
                      "Payment linked to Promise.",
                    )}
                    onClarification={(clarificationType, requestText) => runMutation(
                      () => createPaymentClarification(selected.payment.paymentId, clarificationType, requestText),
                      "Client clarification requested.",
                    )}
                    onPolicy={(managerThresholdCents, executiveThresholdCents) => runMutation(
                      () => savePaymentApprovalPolicy({
                        policyId: approvalResolution?.policy.policyId,
                        policyName: approvalResolution?.policy.policyName || "Standard Payment Approval",
                        activeStatus: "ACTIVE",
                        isDefault: true,
                        verificationRequired: true,
                        approvalRequired: true,
                        twoPersonControlRequired: true,
                        creatorMayVerify: false,
                        verifierMayApprove: false,
                        minimumApprovalAmountCents: 0,
                        managerApprovalThresholdCents: managerThresholdCents,
                        executiveApprovalThresholdCents: executiveThresholdCents,
                        duplicateOverrideRequiresApproval: true,
                        allocationOverrideRequiresApproval: true,
                        overpaymentOverrideRequiresApproval: true,
                        currencyMismatchRequiresApproval: true,
                        reversalRequiresApproval: true,
                        postApprovalCorrectionRequiresApproval: true,
                      }),
                      "Tenant approval thresholds updated.",
                    )}
                  />
                ) : null}
                {tab === "exceptions" ? (
                  <PaymentExceptions
                    paymentId={selected.payment.paymentId}
                    exceptions={scoped.exceptions}
                    disabled={!permissions.manageExceptions || working}
                    onCreate={(category, reason) => runMutation(
                      () => createPaymentException({ paymentId: selected.payment.paymentId, category, reason }),
                      "Payment exception created.",
                    )}
                    onResolve={(exceptionId, resolution) => runMutation(
                      () => updatePaymentException(exceptionId, { status: "RESOLVED", resolution }),
                      "Payment exception resolved.",
                    )}
                  />
                ) : null}
                {tab === "documents" ? (
                  <PaymentDocuments
                    detail={selected}
                    canUpload={permissions.uploadDocuments}
                    canView={permissions.viewDocuments}
                    disabled={working}
                    documentError={documentError}
                    onClearDocumentError={() => setDocumentError("")}
                    onUpload={(file, documentType) => runMutation(
                      () => uploadPaymentDocument(selected.payment.paymentId, file, documentType),
                      "Payment support document uploaded.",
                    )}
                    onDocumentError={(message) => setDocumentError(message)}
                  />
                ) : null}
                {tab === "history" ? <PaymentHistory detail={selected} /> : null}
                {tab === "reporting" ? <PaymentReporting reports={reports} canExport={permissions.exportReport} /> : null}
              </div>
            </>
          )}
        </article>
      </div>

      {isManualOpen ? (
        <ManualPaymentDialog
          onClose={() => setIsManualOpen(false)}
          onSave={async (input) => {
            setWorking(true);
            try {
              const result = await createPayment(input);
              setIsManualOpen(false);
              setMessage(result.replay ? "Existing payment returned; no duplicate was created." : "Canonical payment recorded.");
              await refreshQueue(result.payment.paymentId);
            } catch (cause) {
              setError(cause instanceof Error ? cause.message : "Payment could not be recorded.");
            } finally {
              setWorking(false);
            }
          }}
          working={working}
        />
      ) : null}

      {isUploadOpen ? (
        <PaymentUploadDialog
          uploads={uploads}
          activeUpload={activeUpload}
          working={working}
          canReviewUpload={permissions.reviewUpload}
          onClose={() => { setIsUploadOpen(false); setActiveUpload(null); }}
          onLoad={async () => {
            const result = await listPaymentUploads();
            setUploads(result.uploads);
          }}
          onSelect={async (uploadId) => setActiveUpload(await getPaymentUpload(uploadId))}
          onUpload={async (file, clientId) => {
            setWorking(true);
            try {
              const result = await uploadPaymentFile(file, clientId);
              setActiveUpload(await getPaymentUpload(result.upload.uploadId));
              setUploads((await listPaymentUploads()).uploads);
            } finally {
              setWorking(false);
            }
          }}
          onDecision={async (uploadId, rowId, decision) => {
            await decidePaymentUploadRow(uploadId, rowId, { decision });
            setActiveUpload(await getPaymentUpload(uploadId));
            await refreshQueue();
          }}
        />
      ) : null}

      <style>{PAYMENT_CENTER_STYLE}</style>
    </section>
  );
}

function PaymentMetric({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="min-w-0 bg-white px-4 py-3">
      <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">{label}</p>
      <p className="mt-1 text-xl font-black text-[#0A2540]">{value}</p>
      <p className="mt-0.5 truncate text-[11px] font-semibold text-[#64748B]">{note}</p>
    </div>
  );
}

function PaymentCommandHeader({ payment }: { payment: CanonicalPayment }) {
  return (
    <header className="grid gap-3 border-b border-[#BFEAFF] p-4 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Selected Payment</p>
          <PaymentStatus value={payment.operationalStatus} />
        </div>
        <h3 className="mt-1 truncate text-xl font-black text-[#0A2540]">{payment.customerName || "Unmatched customer"}</h3>
        <p className="mt-1 break-words text-sm font-bold text-[#475569]">
          {payment.accountNumber || "Account not matched"} · {payment.externalPaymentReference || payment.clientPaymentReference || payment.paymentId}
        </p>
      </div>
      <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3 text-left sm:text-right">
        <p className="text-[10px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Reported Amount</p>
        <p className="mt-1 text-2xl font-black text-[#0A2540]">{money(payment.amountCents)}</p>
        <p className="text-xs font-bold text-[#64748B]">{formatDate(payment.paymentDate)}</p>
      </div>
    </header>
  );
}

function PaymentSummary({ detail }: { detail: PaymentDetail }) {
  const payment = detail.payment;
  return (
    <div className="grid gap-4">
      <PaymentSectionHeading eyebrow="Canonical Record" title="Payment identity and source" detail="Immutable identifiers and client-reported remittance context." />
      <dl className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {[
          ["Payment ID", payment.paymentId],
          ["Client", payment.reportedByOrganization || payment.clientId],
          ["Customer", payment.customerName || "Unmatched"],
          ["Account", payment.accountNumber || "Unmatched"],
          ["Source", payment.paymentSource],
          ["Method", payment.paymentMethod],
          ["External reference", payment.externalPaymentReference || "Not supplied"],
          ["Client reference", payment.clientPaymentReference || "Not supplied"],
          ["Reported by", payment.reportedByName],
          ["Reported date", formatDate(payment.reportedDate)],
          ["Allocated", money(payment.allocatedAmountCents)],
          ["Unapplied", money(payment.unappliedAmountCents)],
        ].map(([label, value]) => <PaymentDefinition key={label} label={label} value={value} />)}
      </dl>
      {detail.relatedStatements.length ? (
        <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          <p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Statement Context</p>
          <div className="mt-3 grid gap-2">
            {detail.relatedStatements.map((statement) => (
              <div key={statement.statementId} className="flex flex-wrap justify-between gap-2 rounded-xl border border-[#BFEAFF] bg-white px-3 py-2 text-sm font-bold">
                <span>{statement.statementNumber}</span>
                <span>{money(statement.balanceCents)} · {statement.lifecycleStatus}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function PaymentMatching({ detail, disabled, onConfirm }: {
  detail: PaymentDetail;
  disabled: boolean;
  onConfirm: (suggestion: PaymentDetail["matchingSuggestions"][number]) => void;
}) {
  return (
    <div className="grid gap-4">
      <PaymentSectionHeading eyebrow="Controlled Matching" title="Suggested canonical records" detail="Suggestions remain advisory until an authorized operator confirms the match." />
      {detail.matchingSuggestions.length === 0 ? <PaymentEmpty icon={Link2} title="No match suggestions" detail="This payment needs additional account or customer context." /> : null}
      {detail.matchingSuggestions.map((suggestion) => (
        <div key={suggestion.suggestionId} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          <div>
            <div className="flex flex-wrap gap-2"><PaymentStatus value={suggestion.confidence} /><PaymentStatus value={suggestion.entityType} /></div>
            <p className="mt-2 font-black text-[#0A2540]">{suggestion.entityLabel}</p>
            <p className="mt-1 text-xs font-semibold text-[#475569]">{suggestion.factors.join(" · ") || "No matching factors recorded"}</p>
          </div>
          <button className="payment-button payment-button-primary" type="button" disabled={disabled} onClick={() => onConfirm(suggestion)}>
            <Check className="size-4" aria-hidden="true" />
            Confirm Match
          </button>
        </div>
      ))}
    </div>
  );
}

function PaymentAllocationPanel({ detail, clientAllocations, clarifications, disabled, onAllocate, onReverse }: {
  detail: PaymentDetail;
  clientAllocations: PaymentClientAllocationInstruction[];
  clarifications: PaymentClarification[];
  disabled: boolean;
  onAllocate: (input: { accountId: string; invoiceId?: string; invoiceNumber?: string; amountCents: number; currency: string; allocationType: string }) => void;
  onReverse: (allocationId: string, reason: string) => void;
}) {
  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [amount, setAmount] = useState("");
  const payment = detail.payment;
  const openClarifications = clarifications.filter((item) => item.status !== "RESOLVED");
  return (
    <div className="grid gap-4">
      <PaymentSectionHeading eyebrow="Allocation Ledger" title="Record allocation context without changing payment identity" detail={`${money(payment.unappliedAmountCents)} remains available for controlled allocation.`} />
      <section className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <p className="text-[11px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Client Allocation Instructions</p>
        <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">
          Client-submitted allocation context. This is governance information only; it does not post cash application to the ERP.
        </p>
        {clientAllocations.length ? (
          <div className="mt-3 grid gap-2">
            {clientAllocations.map((instruction) => (
              <div key={instruction.instructionId} className="rounded-xl border border-[#BFEAFF] bg-white p-3">
                <div className="flex flex-wrap justify-between gap-2">
                  <p className="font-black text-[#0A2540]">{instruction.invoiceNumber}</p>
                  <PaymentStatus value={instruction.reviewStatus} warning={instruction.reviewStatus !== "VERIFIED"} />
                </div>
                <dl className="mt-2 grid gap-1 text-xs font-semibold text-[#475569] sm:grid-cols-2">
                  <div>Submission status: Submitted</div>
                  <div>Submitted: {formatDateTime(instruction.submittedAt)}</div>
                  <div>Due date: {instruction.dueDate ? formatDate(instruction.dueDate) : "Not recorded"}</div>
                  <div>Operational balance: {money(instruction.operationalBalanceCents)}</div>
                  <div>Reported allocation: {money(instruction.reportedAllocationCents)}</div>
                  <div>Review status: {instruction.reviewStatus}</div>
                </dl>
                {instruction.clientNote ? <p className="mt-2 text-sm font-semibold leading-6 text-[#475569]">Client note: {instruction.clientNote}</p> : null}
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm font-semibold text-[#64748B]">No client allocation instructions are recorded for this payment.</p>
        )}
        {openClarifications.length ? (
          <p className="mt-3 rounded-xl border border-[#FED7AA] bg-white px-3 py-2 text-xs font-bold text-[#9A3412]">
            Related clarification state: {openClarifications.map((item) => `${item.clarificationType} (${item.status})`).join(" · ")}
          </p>
        ) : null}
      </section>
      <form
        className="grid gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4 sm:grid-cols-[minmax(0,1fr)_minmax(0,.7fr)_auto]"
        onSubmit={(event) => {
          event.preventDefault();
          onAllocate({
            accountId: payment.accountId || "",
            invoiceNumber: invoiceNumber.trim() || undefined,
            amountCents: Math.round(Number(amount) * 100),
            currency: payment.currency,
            allocationType: invoiceNumber.trim() ? "INVOICE" : "ACCOUNT",
          });
        }}
      >
        <label className="payment-label">Invoice number<input className="payment-input" value={invoiceNumber} onChange={(event) => setInvoiceNumber(event.target.value)} placeholder="Optional account-level allocation" /></label>
        <label className="payment-label">Amount<input className="payment-input" type="number" min="0.01" step="0.01" max={payment.unappliedAmountCents / 100} required value={amount} onChange={(event) => setAmount(event.target.value)} /></label>
        <button className="payment-button payment-button-primary self-end" type="submit" disabled={disabled || !payment.accountId || !amount}>Allocate</button>
      </form>
      <div className="grid gap-2">
        {detail.allocations.map((allocation) => (
          <div key={allocation.allocationId} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#BFEAFF] p-4">
            <div>
              <p className="font-black text-[#0A2540]">{allocation.invoiceNumber || "Account-level allocation"}</p>
              <p className="mt-1 text-xs font-bold text-[#64748B]">{money(allocation.allocatedAmountCents)} · {allocation.allocationStatus}</p>
            </div>
            {allocation.allocationStatus !== "REVERSED" ? (
              <button className="payment-button payment-button-danger" type="button" disabled={disabled} onClick={() => {
                const reason = window.prompt("Reason for reversing this allocation:");
                if (reason?.trim()) onReverse(allocation.allocationId, reason.trim());
              }}>Reverse</button>
            ) : null}
          </div>
        ))}
        {detail.allocations.length === 0 ? <PaymentEmpty icon={CircleDollarSign} title="No allocations" detail="The payment remains unallocated in Payment Center until an operator records governed allocation context." /> : null}
      </div>
    </div>
  );
}

function PaymentVerification({ detail, permissions, disabled, onAction }: {
  detail: PaymentDetail;
  permissions: PaymentPermissions;
  disabled: boolean;
  onAction: (action: "start_verification" | "verify" | "approve" | "reject" | "reverse" | "confirm_duplicate" | "mark_distinct", reason: string) => void;
}) {
  const payment = detail.payment;
  const controls = [
    { action: "start_verification" as const, label: "Start Verification", allowed: permissions.verify },
    { action: "verify" as const, label: "Verify Payment", allowed: permissions.verify },
    { action: "approve" as const, label: "Approve", allowed: permissions.approve },
    { action: "reject" as const, label: "Reject", allowed: permissions.reject },
    { action: "mark_distinct" as const, label: "Mark Distinct", allowed: permissions.overrideDuplicate },
    { action: "confirm_duplicate" as const, label: "Confirm Duplicate", allowed: permissions.reviewDuplicate },
    { action: "reverse" as const, label: "Reverse Payment", allowed: permissions.reverse },
  ];
  return (
    <div className="grid gap-4">
      <PaymentSectionHeading eyebrow="Four-Eyes Control" title="Verification and approval" detail="Every transition is permission-gated, reasoned, timestamped, and auditable." />
      <dl className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        <PaymentDefinition label="Operational" value={payment.operationalStatus} />
        <PaymentDefinition label="Verification" value={payment.verificationStatus} />
        <PaymentDefinition label="Duplicate" value={payment.duplicateStatus} />
        <PaymentDefinition label="Exception" value={payment.exceptionStatus} />
      </dl>
      <div className="flex flex-wrap gap-2">
        {controls.map((control) => (
          <button
            key={control.action}
            type="button"
            className={`payment-button ${control.action === "reject" || control.action === "reverse" ? "payment-button-danger" : "payment-button-primary"}`}
            disabled={disabled || !control.allowed}
            onClick={() => {
              const reason = window.prompt(`Reason for ${control.label.toLowerCase()}:`);
              if (reason?.trim()) onAction(control.action, reason.trim());
            }}
          >
            {control.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaymentOperationalControls({
  payment,
  approval,
  promiseLinks,
  clarifications,
  corrections,
  permissions,
  manageApprovalPolicy,
  actorUserId,
  disabled,
  onSelectPayment,
  onCorrection,
  onCorrectionDecision,
  onPromiseLink,
  onClarification,
  onPolicy,
}: {
  payment: CanonicalPayment;
  approval: PaymentApprovalResolution | null;
  promiseLinks: PaymentPromiseLink[];
  clarifications: PaymentClarification[];
  corrections: PaymentCorrection[];
  permissions: PaymentPermissions;
  manageApprovalPolicy: boolean;
  actorUserId: string;
  disabled: boolean;
  onSelectPayment: (paymentId: string) => void;
  onCorrection: (correctionType: string, reason: string) => void;
  onCorrectionDecision: (correctionId: string, decision: "APPROVE" | "REJECT", reason: string) => void;
  onPromiseLink: (promiseId: string, promisedAmountCents: number, promiseDueDate: string) => void;
  onClarification: (clarificationType: string, requestText: string) => void;
  onPolicy: (managerThresholdCents: number | null, executiveThresholdCents: number | null) => void;
}) {
  return (
    <div className="grid gap-5">
      <PaymentSectionHeading
        eyebrow="Operational Governance"
        title="Policy, correction, Promise, and client clarification"
        detail="The active approval policy and linked operational records remain company-scoped, immutable, and independently auditable."
      />
      {payment.replacementPaymentId ? (
        <div className="rounded-2xl border border-[#FED7AA] bg-[#FFF7ED] px-4 py-3 text-sm font-semibold leading-6 text-[#9A3412]">
          Original payment reversed by correction. Replacement payment:{" "}
          <button type="button" className="font-black underline focus-ring" onClick={() => onSelectPayment(payment.replacementPaymentId!)}>
            {payment.replacementPaymentId}
          </button>
        </div>
      ) : null}
      {payment.correctionOfPaymentId ? (
        <div className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] px-4 py-3 text-sm font-semibold leading-6 text-[#475569]">
          Replacement payment for correction of{" "}
          <button type="button" className="font-black text-[#0A2540] underline focus-ring" onClick={() => onSelectPayment(payment.correctionOfPaymentId!)}>
            {payment.correctionOfPaymentId}
          </button>
        </div>
      ) : null}
      <section className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Approval Policy</p>
            <h4 className="mt-1 text-base font-black text-[#0A2540]">{approval?.policy.policyName || "Policy resolution required"}</h4>
          </div>
          <div className="flex flex-wrap gap-2">
            <PaymentStatus value={approval?.requiredApprovalLevel || "PENDING"} warning={Boolean(approval?.blockedReason)} />
            <button className="payment-button payment-button-secondary" type="button" disabled={disabled || !manageApprovalPolicy} onClick={() => {
              const manager = window.prompt("Manager approval threshold (blank for none):", approval?.policy.managerApprovalThresholdCents == null ? "" : String(approval.policy.managerApprovalThresholdCents / 100));
              if (manager == null) return;
              const elevated = window.prompt("Elevated approval threshold (blank for none):", approval?.policy.executiveApprovalThresholdCents == null ? "" : String(approval.policy.executiveApprovalThresholdCents / 100));
              if (elevated == null) return;
              const managerCents = manager.trim() ? Math.round(Number(manager) * 100) : null;
              const elevatedCents = elevated.trim() ? Math.round(Number(elevated) * 100) : null;
              if ((managerCents != null && managerCents < 0) || (elevatedCents != null && elevatedCents < 0)) return;
              onPolicy(managerCents, elevatedCents);
            }}>Configure Thresholds</button>
          </div>
        </div>
        <dl className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
          <PaymentDefinition label="Policy version" value={approval ? `Version ${approval.policy.version}` : "Not resolved"} />
          <PaymentDefinition label="Payment amount" value={money(payment.amountCents)} />
          <PaymentDefinition label="Approval stage" value={approval?.currentApprovalStage || "Pending"} />
          <PaymentDefinition label="Outstanding" value={approval?.outstandingApprovals.join(", ") || "None"} />
        </dl>
        {approval?.blockedReason ? <p className="mt-3 rounded-xl border border-[#FED7AA] bg-white px-3 py-2 text-xs font-bold text-[#9A3412]">{approval.blockedReason}</p> : null}
      </section>

      <section className="grid gap-3 rounded-2xl border border-[#BFEAFF] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className="text-[11px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Correction Lifecycle</p><h4 className="mt-1 font-black text-[#0A2540]">Preserve the approved original</h4></div>
          <button className="payment-button payment-button-secondary" type="button" disabled={disabled || !permissions.edit || payment.operationalStatus !== "APPROVED"} onClick={() => {
            const correctionType = window.prompt("Correction type:", "REFERENCE");
            const reason = window.prompt("Correction reason:");
            if (correctionType?.trim() && reason?.trim()) onCorrection(correctionType.trim(), reason.trim());
          }}>Request Correction</button>
        </div>
        <p className="text-sm font-semibold leading-6 text-[#475569]">Approved records are never overwritten. Material corrections use an approved reversal-and-replacement record linked to this payment.</p>
        <div className="grid gap-2">
          {corrections.map((correction) => {
            const selfApproval = correction.requestedByUserId === actorUserId;
            const canDecide = permissions.approve && correction.approvalStatus === "PENDING" && !selfApproval;
            return (
              <div key={correction.correctionId} className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
                <div className="flex flex-wrap justify-between gap-2">
                  <p className="font-black text-[#0A2540]">{correction.correctionType.replaceAll("_", " ")}</p>
                  <PaymentStatus value={correction.approvalStatus} warning={correction.approvalStatus === "PENDING"} />
                </div>
                <dl className="mt-2 grid gap-1 text-xs font-semibold text-[#475569] sm:grid-cols-2">
                  <div>Correction ID: {correction.correctionId}</div>
                  <div>Requested by: {correction.requestedByName}</div>
                  <div>Requested: {formatDateTime(correction.requestedAt)}</div>
                  <div>Original payment: {correction.originalPaymentId}</div>
                  <div>Replacement payment: {correction.replacementPaymentId || "Pending approval"}</div>
                </dl>
                <p className="mt-2 text-sm font-semibold leading-6 text-[#475569]">{correction.reason}</p>
                {correction.approvedByName ? <p className="mt-1 text-xs font-bold text-[#64748B]">Approved by {correction.approvedByName} · {correction.approvedAt ? formatDateTime(correction.approvedAt) : ""}</p> : null}
                {correction.rejectedByName ? <p className="mt-1 text-xs font-bold text-[#64748B]">Rejected by {correction.rejectedByName} · {correction.rejectionReason || ""}</p> : null}
                {selfApproval && correction.approvalStatus === "PENDING" ? (
                  <p className="mt-2 rounded-lg border border-[#FED7AA] bg-white px-3 py-2 text-xs font-bold text-[#9A3412]">You requested this correction and cannot approve it.</p>
                ) : null}
                <div className="mt-3 flex flex-wrap gap-2">
                  {correction.replacementPaymentId ? (
                    <button type="button" className="payment-button payment-button-secondary" onClick={() => onSelectPayment(correction.replacementPaymentId!)}>
                      Open Replacement Payment
                    </button>
                  ) : null}
                  {correction.approvalStatus === "PENDING" ? (
                    <>
                      <button type="button" className="payment-button payment-button-primary" disabled={disabled || !canDecide} onClick={() => {
                        const reason = window.prompt("Approval reason:");
                        if (reason?.trim()) onCorrectionDecision(correction.correctionId, "APPROVE", reason.trim());
                      }}>Approve Correction</button>
                      <button type="button" className="payment-button payment-button-danger" disabled={disabled || !canDecide} onClick={() => {
                        const reason = window.prompt("Rejection reason:");
                        if (reason?.trim()) onCorrectionDecision(correction.correctionId, "REJECT", reason.trim());
                      }}>Reject Correction</button>
                    </>
                  ) : null}
                </div>
              </div>
            );
          })}
          {corrections.length === 0 ? <p className="text-sm font-semibold text-[#64748B]">No correction requests are recorded for this payment.</p> : null}
        </div>
      </section>

      <section className="grid gap-3 rounded-2xl border border-[#BFEAFF] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className="text-[11px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Promise Relationship</p><h4 className="mt-1 font-black text-[#0A2540]">Controlled Promise outcome</h4></div>
          <button className="payment-button payment-button-secondary" type="button" disabled={disabled || !permissions.match} onClick={() => {
            const promiseId = window.prompt("Promise ID:");
            const amount = window.prompt("Promised amount:");
            const dueDate = window.prompt("Promise due date (YYYY-MM-DD):");
            const amountCents = Math.round(Number(amount || 0) * 100);
            if (promiseId?.trim() && amountCents > 0 && dueDate?.trim()) onPromiseLink(promiseId.trim(), amountCents, dueDate.trim());
          }}>Link Promise</button>
        </div>
        <div className="grid gap-2">
          {promiseLinks.map((link) => (
            <div key={link.linkId} className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
              <div className="flex flex-wrap justify-between gap-2"><p className="font-black text-[#0A2540]">{link.promiseReference || link.promiseId}</p><PaymentStatus value={link.confirmedOutcome || link.recommendedOutcome} /></div>
              <p className="mt-1 text-xs font-semibold text-[#475569]">{money(link.promisedAmountCents)} due {link.promiseDueDate || "Not recorded"} · {link.confidence} confidence</p>
            </div>
          ))}
          {promiseLinks.length === 0 ? <p className="text-sm font-semibold text-[#64748B]">No Promise relationship is recorded.</p> : null}
        </div>
      </section>

      <section className="grid gap-3 rounded-2xl border border-[#BFEAFF] p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div><p className="text-[11px] font-black uppercase tracking-[.14em] text-[#14B8A6]">Client Clarification</p><h4 className="mt-1 font-black text-[#0A2540]">Structured information request</h4></div>
          <button className="payment-button payment-button-secondary" type="button" disabled={disabled || !permissions.manageExceptions} onClick={() => {
            const clarificationType = window.prompt("Clarification type:", "PAYMENT_REFERENCE");
            const requestText = window.prompt("Information requested from the client:");
            if (clarificationType?.trim() && requestText?.trim()) onClarification(clarificationType.trim(), requestText.trim());
          }}>Request Clarification</button>
        </div>
        <div className="grid gap-2">
          {clarifications.map((item) => (
            <div key={item.clarificationId} className="rounded-xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
              <div className="flex flex-wrap justify-between gap-2"><p className="font-black text-[#0A2540]">{item.clarificationType.replaceAll("_", " ")}</p><PaymentStatus value={item.status} warning={item.status !== "RESOLVED"} /></div>
              <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">{item.requestText}</p>
              {item.clientResponse ? <p className="mt-2 rounded-lg bg-white px-3 py-2 text-xs font-semibold text-[#0A2540]">Client response: {item.clientResponse}</p> : null}
            </div>
          ))}
          {clarifications.length === 0 ? <p className="text-sm font-semibold text-[#64748B]">No clarification requests are open.</p> : null}
        </div>
      </section>
    </div>
  );
}

function PaymentExceptions({ paymentId, exceptions, disabled, onCreate, onResolve }: {
  paymentId: string;
  exceptions: PaymentException[];
  disabled: boolean;
  onCreate: (category: string, reason: string) => void;
  onResolve: (exceptionId: string, resolution: string) => void;
}) {
  const [resolutionDraft, setResolutionDraft] = useState<Record<string, string>>({});
  return (
    <div className="grid gap-4">
      <PaymentSectionHeading eyebrow="Exception Resolution" title="Operational issues" detail="Capture missing information, ownership, and resolution separately from the financial record." />
      <button className="payment-button payment-button-primary w-fit" type="button" disabled={disabled} onClick={() => {
        const category = window.prompt("Exception category:", "MISSING_INFORMATION");
        const reason = window.prompt("Describe the exception:");
        if (category?.trim() && reason?.trim()) onCreate(category.trim(), reason.trim());
      }}>Create Exception</button>
      {exceptions.map((exception) => {
        const resolved = ["RESOLVED", "REJECTED", "CLOSED"].includes(exception.status);
        return (
          <div key={exception.exceptionId} className="rounded-2xl border border-[#F5C2B0] bg-[#FFF8F5] p-4">
            <div className="flex flex-wrap justify-between gap-2"><PaymentStatus value={exception.status} warning={!resolved} /><span className="text-xs font-bold text-[#64748B]">{formatDate(exception.createdAt)}</span></div>
            <p className="mt-2 font-black text-[#0A2540]">{exception.category}</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">{exception.reason}</p>
            {exception.resolution ? <p className="mt-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-[#0A2540]">Resolution: {exception.resolution}</p> : null}
            {exception.resolvedByName ? <p className="mt-1 text-xs font-bold text-[#64748B]">Resolved by {exception.resolvedByName}{exception.resolvedAt ? ` · ${formatDateTime(exception.resolvedAt)}` : ""}</p> : null}
            {!resolved ? (
              <div className="mt-3 grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto]">
                <label className="payment-label">
                  Resolution note
                  <input
                    className="payment-input"
                    value={resolutionDraft[exception.exceptionId] || ""}
                    onChange={(event) => setResolutionDraft((current) => ({ ...current, [exception.exceptionId]: event.target.value }))}
                    placeholder="Required resolution note"
                  />
                </label>
                <button
                  className="payment-button payment-button-primary self-end"
                  type="button"
                  disabled={disabled || !(resolutionDraft[exception.exceptionId] || "").trim()}
                  onClick={() => {
                    const resolution = (resolutionDraft[exception.exceptionId] || "").trim();
                    if (!resolution) return;
                    onResolve(exception.exceptionId, resolution);
                    setResolutionDraft((current) => ({ ...current, [exception.exceptionId]: "" }));
                  }}
                >
                  Resolve Exception
                </button>
              </div>
            ) : null}
          </div>
        );
      })}
      {exceptions.length === 0 ? <PaymentEmpty icon={BadgeCheck} title="No open exceptions" detail={`Payment ${paymentId} has no recorded exception workflow.`} /> : null}
    </div>
  );
}

function PaymentDocuments({ detail, canUpload, canView, disabled, documentError, onClearDocumentError, onUpload, onDocumentError }: {
  detail: PaymentDetail;
  canUpload: boolean;
  canView: boolean;
  disabled: boolean;
  documentError: string;
  onClearDocumentError: () => void;
  onUpload: (file: File, documentType: string) => void;
  onDocumentError: (message: string) => void;
}) {
  async function openDocument(documentId: string) {
    onClearDocumentError();
    if (!canView) {
      onDocumentError("Supporting-document access required.");
      return;
    }
    try {
      const response = await fetch(getPaymentDocumentUrl(detail.payment.paymentId, documentId), { credentials: "include" });
      if (!response.ok) throw new Error((await response.text()) || "Supporting document could not be opened.");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (cause) {
      onDocumentError(cause instanceof Error ? cause.message : "Supporting document could not be opened.");
    }
  }

  return (
    <div className="grid gap-4">
      <PaymentSectionHeading eyebrow="Secure Documents" title="Payment support evidence" detail="Receipts, remittance detail, deposit support, and correspondence remain tenant-scoped and hash-verified." />
      {documentError ? <PaymentNotice tone="error" onClose={onClearDocumentError}>{documentError}</PaymentNotice> : null}
      <label className={`payment-button payment-button-primary w-fit ${disabled || !canUpload ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
        <Upload className="size-4" aria-hidden="true" />
        Upload Document
        <input className="sr-only" type="file" disabled={disabled || !canUpload} onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) onUpload(file, "PAYMENT_SUPPORT");
          event.target.value = "";
        }} />
      </label>
      <div className="grid gap-2 sm:grid-cols-2">
        {detail.documents.map((document) => (
          <div key={document.documentId} className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
            <FileText className="size-5 text-[#14B8A6]" aria-hidden="true" />
            <p className="mt-2 break-all font-black text-[#0A2540]">{document.fileName}</p>
            <p className="mt-1 text-xs font-bold text-[#64748B]">{document.documentType} · {formatBytes(document.fileSize)}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              <button className="payment-button payment-button-secondary" type="button" disabled={!canView} onClick={() => void openDocument(document.documentId)}>Open</button>
              <a
                className={`payment-button payment-button-secondary ${canView ? "" : "pointer-events-none opacity-50"}`}
                href={canView ? getPaymentDocumentUrl(detail.payment.paymentId, document.documentId) : undefined}
                download={canView ? document.fileName : undefined}
              >
                <Download className="size-4" aria-hidden="true" />
                Download
              </a>
            </div>
          </div>
        ))}
      </div>
      {detail.documents.length === 0 ? <PaymentEmpty icon={FileText} title="No support documents" detail="Upload evidence only when it supports this payment record." /> : null}
    </div>
  );
}

function PaymentHistory({ detail }: { detail: PaymentDetail }) {
  return (
    <div className="grid gap-4">
      <PaymentSectionHeading eyebrow="Permanent Audit" title="Payment history" detail="Chronological actions preserve actor, state transition, reason, and correlation ID." />
      <ol className="relative grid gap-0 border-l-2 border-[#BFEAFF] pl-5">
        {detail.history.map((entry) => (
          <li key={entry.actionId} className="relative border-b border-[#E5F5FC] py-4 last:border-0">
            <span className="absolute -left-[1.72rem] top-5 size-3 rounded-full border-2 border-white bg-[#14B8A6] shadow-[0_0_0_2px_#BFEAFF]" />
            <div className="flex flex-wrap justify-between gap-2">
              <p className="font-black text-[#0A2540]">{entry.actionType.replaceAll("_", " ")}</p>
              <time className="text-xs font-bold text-[#64748B]">{formatDateTime(entry.occurredAt)}</time>
            </div>
            <p className="mt-1 text-sm font-semibold text-[#475569]">{entry.actorName} · {entry.previousStatus || "Initial"} → {entry.newStatus || "Recorded"}</p>
            {entry.reason ? <p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">{entry.reason}</p> : null}
            <p className="mt-1 break-all text-[10px] font-bold uppercase tracking-[.1em] text-[#94A3B8]">{entry.correlationId}</p>
          </li>
        ))}
      </ol>
      {detail.permissions.viewAudit ? (
        <section className="rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-4">
          <h4 className="text-sm font-black text-[#0A2540]">Audit events</h4>
          <div className="mt-3 grid gap-2">
            {detail.audit.map((entry) => (
              <div key={entry.auditId} className="rounded-xl border border-[#BFEAFF] bg-white p-3">
                <div className="flex flex-wrap justify-between gap-2">
                  <p className="text-xs font-black text-[#0A2540]">{entry.eventType}</p>
                  <PaymentStatus value={entry.outcome} warning={entry.outcome !== "success" && entry.outcome !== "recorded"} />
                </div>
                <p className="mt-1 text-xs font-semibold text-[#475569]">{entry.actorName} · {entry.actorRole} · {formatDateTime(entry.occurredAt)}</p>
                <p className="mt-1 break-all text-[10px] font-bold uppercase tracking-[.1em] text-[#94A3B8]">{entry.correlationId}</p>
              </div>
            ))}
            {detail.audit.length === 0 ? <p className="text-xs font-semibold text-[#64748B]">No restricted audit events are available for this payment.</p> : null}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function PaymentReporting({ reports, canExport }: { reports: Awaited<ReturnType<typeof getPaymentReports>> | null; canExport: boolean }) {
  if (!reports) return <PaymentLoading label="Loading payment reporting" />;
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PaymentSectionHeading eyebrow="Operational Reporting" title="Payment intake and governance totals" detail="Canonical totals only. Fees, commissions, settlements, and provider deposits are intentionally excluded." />
        {canExport ? (
          <a className="payment-button payment-button-secondary" href="/api/ar-recovery/payments/reports?format=csv">
            <Download className="size-4" aria-hidden="true" />
            Export CSV
          </a>
        ) : (
          <button className="payment-button payment-button-secondary" type="button" disabled>
            <Download className="size-4" aria-hidden="true" />
            Export CSV
          </button>
        )}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <ReportingGroup title="By source" rows={reports.bySource} />
        <ReportingGroup title="By method" rows={reports.byMethod} />
        <ReportingGroup title="By client" rows={reports.byClient} />
        <ReportingGroup title="Exceptions" rows={reports.exceptions} />
      </div>
    </div>
  );
}

function ReportingGroup({ title, rows }: { title: string; rows: Array<{ label: string; count: number; amountCents: number }> }) {
  return (
    <section className="rounded-2xl border border-[#BFEAFF] p-4">
      <h4 className="text-sm font-black text-[#0A2540]">{title}</h4>
      <div className="mt-3 grid gap-2">
        {rows.map((row) => (
          <div key={row.label} className="grid grid-cols-[minmax(0,1fr)_auto_auto] gap-3 border-t border-[#E5F5FC] pt-2 text-xs font-bold">
            <span className="truncate">{row.label}</span><span>{row.count}</span><span>{money(row.amountCents)}</span>
          </div>
        ))}
        {rows.length === 0 ? <p className="text-xs font-semibold text-[#64748B]">No records available.</p> : null}
      </div>
    </section>
  );
}

function ManualPaymentDialog({ onClose, onSave, working }: {
  onClose: () => void;
  onSave: (input: Parameters<typeof createPayment>[0]) => Promise<void>;
  working: boolean;
}) {
  const [form, setForm] = useState({
    clientId: "",
    customerName: "",
    customerNumber: "",
    accountId: "",
    accountNumber: "",
    paymentSource: "Internal Manual",
    externalPaymentReference: "",
    clientPaymentReference: "",
    paymentDate: new Date().toISOString().slice(0, 10),
    amount: "",
    currency: "USD",
    paymentMethod: "ACH",
    payerName: "",
    memo: "",
    reportedByOrganization: "",
  });
  function update(key: keyof typeof form, value: string) {
    setForm((current) => ({ ...current, [key]: value }));
  }
  return (
    <PaymentDialog title="Record client-reported payment" detail="Create one canonical payment record. Allocation and approval remain separate controlled actions." onClose={onClose}>
      <form className="grid gap-4" onSubmit={(event) => {
        event.preventDefault();
        void onSave({
          clientId: form.clientId.trim(),
          customerName: form.customerName.trim(),
          customerNumber: form.customerNumber.trim(),
          accountId: form.accountId.trim() || undefined,
          accountNumber: form.accountNumber.trim(),
          paymentSource: form.paymentSource,
          externalPaymentReference: form.externalPaymentReference.trim(),
          clientPaymentReference: form.clientPaymentReference.trim(),
          paymentDate: form.paymentDate,
          amountCents: Math.round(Number(form.amount) * 100),
          currency: form.currency,
          paymentMethod: form.paymentMethod,
          payerName: form.payerName.trim(),
          memo: form.memo.trim(),
          reportedByOrganization: form.reportedByOrganization.trim(),
          idempotencyKey: `manual:${form.clientId}:${form.externalPaymentReference || form.clientPaymentReference}:${form.amount}:${form.paymentDate}`,
        });
      }}>
        <div className="grid gap-3 sm:grid-cols-2">
          <PaymentField label="Client ID" required value={form.clientId} onChange={(value) => update("clientId", value)} />
          <PaymentField label="Reporting organization" required value={form.reportedByOrganization} onChange={(value) => update("reportedByOrganization", value)} />
          <PaymentField label="Customer name" required value={form.customerName} onChange={(value) => update("customerName", value)} />
          <PaymentField label="Customer number" value={form.customerNumber} onChange={(value) => update("customerNumber", value)} />
          <PaymentField label="Account ID" value={form.accountId} onChange={(value) => update("accountId", value)} />
          <PaymentField label="Account number" value={form.accountNumber} onChange={(value) => update("accountNumber", value)} />
          <PaymentField label="External reference" value={form.externalPaymentReference} onChange={(value) => update("externalPaymentReference", value)} />
          <PaymentField label="Client reference" value={form.clientPaymentReference} onChange={(value) => update("clientPaymentReference", value)} />
          <PaymentField label="Payment date" type="date" required value={form.paymentDate} onChange={(value) => update("paymentDate", value)} />
          <PaymentField label="Amount" type="number" required value={form.amount} onChange={(value) => update("amount", value)} />
          <label className="payment-label">Method<select className="payment-input" value={form.paymentMethod} onChange={(event) => update("paymentMethod", event.target.value)}><option>ACH</option><option>Check</option><option>Wire</option><option>Credit Card</option><option>Cash</option><option>Other</option></select></label>
          <PaymentField label="Payer name" value={form.payerName} onChange={(value) => update("payerName", value)} />
        </div>
        <label className="payment-label">Memo<textarea className="payment-input min-h-24 resize-y" value={form.memo} onChange={(event) => update("memo", event.target.value)} /></label>
        <div className="flex justify-end gap-2">
          <button className="payment-button payment-button-secondary" type="button" onClick={onClose}>Cancel</button>
          <button className="payment-button payment-button-primary" type="submit" disabled={working}>
            {working ? <LoaderCircle className="size-4 animate-spin" aria-hidden="true" /> : <Check className="size-4" aria-hidden="true" />}
            Record Payment
          </button>
        </div>
      </form>
    </PaymentDialog>
  );
}

function PaymentUploadDialog({ uploads, activeUpload, working, canReviewUpload, onClose, onLoad, onSelect, onUpload, onDecision }: {
  uploads: PaymentUpload[];
  activeUpload: { upload: PaymentUpload; rows: PaymentUploadRow[] } | null;
  working: boolean;
  canReviewUpload: boolean;
  onClose: () => void;
  onLoad: () => Promise<void>;
  onSelect: (uploadId: string) => Promise<void>;
  onUpload: (file: File, clientId: string) => Promise<void>;
  onDecision: (uploadId: string, rowId: string, decision: "accept" | "reject" | "exclude" | "reprocess") => Promise<void>;
}) {
  const [clientId, setClientId] = useState("");
  useEffect(() => { void onLoad(); }, []);
  return (
    <PaymentDialog title="Payment file intake" detail="Upload CSV or XLSX, review normalized rows, and intentionally accept only valid records." onClose={onClose} wide>
      <div className="grid min-w-0 gap-4 lg:grid-cols-[18rem_minmax(0,1fr)]">
        <aside className="grid content-start gap-3 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3">
          <PaymentField label="Client ID" required value={clientId} onChange={setClientId} />
          <label className={`payment-button payment-button-primary justify-center ${working || !clientId.trim() ? "pointer-events-none opacity-50" : "cursor-pointer"}`}>
            <Upload className="size-4" aria-hidden="true" />
            Upload CSV or XLSX
            <input className="sr-only" type="file" accept=".csv,.xlsx" disabled={working || !clientId.trim()} onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void onUpload(file, clientId.trim());
              event.target.value = "";
            }} />
          </label>
          <a className="payment-button payment-button-secondary justify-center" href="/api/ar-recovery/payments/uploads/template">
            <Download className="size-4" aria-hidden="true" />
            CSV Template
          </a>
          <div className="grid gap-2 border-t border-[#BFEAFF] pt-3">
            {uploads.map((upload) => (
              <button className="rounded-xl border border-[#BFEAFF] bg-white p-3 text-left focus-ring" type="button" key={upload.uploadId} onClick={() => void onSelect(upload.uploadId)}>
                <p className="truncate text-xs font-black text-[#0A2540]">{upload.filename}</p>
                <p className="mt-1 text-[11px] font-bold text-[#64748B]">{upload.rowCount} rows · {upload.processingStatus}</p>
              </button>
            ))}
          </div>
        </aside>
        <div className="min-w-0">
          {!activeUpload ? <PaymentEmpty icon={FileSpreadsheet} title="Select an intake batch" detail="Review every row before it becomes a canonical payment." /> : (
            <div className="grid gap-3">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div><p className="font-black text-[#0A2540]">{activeUpload.upload.filename}</p><p className="text-xs font-bold text-[#64748B]">{activeUpload.upload.validRowCount} valid · {activeUpload.upload.invalidRowCount} invalid · {activeUpload.upload.duplicateRowCount} duplicate</p></div>
                <PaymentStatus value={activeUpload.upload.processingStatus} />
              </div>
              <div className="max-h-[32rem] overflow-auto rounded-2xl border border-[#BFEAFF]">
                <table className="w-full min-w-[46rem] border-collapse text-left text-xs">
                  <thead className="sticky top-0 bg-[#0A2540] text-white"><tr><th className="p-3">Row</th><th className="p-3">Customer / Account</th><th className="p-3">Amount</th><th className="p-3">Validation</th><th className="p-3">Decision</th></tr></thead>
                  <tbody>
                    {activeUpload.rows.map((row) => (
                      <tr key={row.rowId} className="border-t border-[#BFEAFF] align-top">
                        <td className="p-3 font-black">{row.rowNumber}</td>
                        <td className="p-3"><p className="font-bold">{String(row.normalized.customerName || "")}</p><p>{String(row.normalized.accountNumber || "")}</p></td>
                        <td className="p-3 font-bold">{money(Number(row.normalized.amountCents || 0))}</td>
                        <td className="p-3"><PaymentStatus value={row.validationStatus} warning={row.validationStatus !== "VALID"} />{row.validationErrors.length ? <p className="mt-1 max-w-xs text-[11px] text-[#9A3412]">{row.validationErrors.join("; ")}</p> : null}</td>
                        <td className="p-3"><div className="flex gap-1"><button className="payment-row-action" type="button" disabled={!canReviewUpload || row.validationStatus !== "VALID"} onClick={() => void onDecision(activeUpload.upload.uploadId, row.rowId, "accept")}>Accept</button><button className="payment-row-action" type="button" disabled={!canReviewUpload} onClick={() => void onDecision(activeUpload.upload.uploadId, row.rowId, "reject")}>Reject</button></div></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </PaymentDialog>
  );
}

function PaymentDialog({ title, detail, onClose, wide = false, children }: { title: string; detail: string; onClose: () => void; wide?: boolean; children: ReactNode }) {
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center overflow-y-auto bg-[#02072C]/70 p-3 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label={title}>
      <section className={`my-auto w-full overflow-hidden rounded-[1.5rem] border border-[#BFEAFF] bg-white shadow-[0_30px_90px_rgba(2,7,44,.35)] ${wide ? "max-w-6xl" : "max-w-3xl"}`}>
        <header className="flex items-start justify-between gap-4 border-b border-[#BFEAFF] px-5 py-4">
          <div><p className="text-[11px] font-black uppercase tracking-[.16em] text-[#14B8A6]">Payment Center</p><h2 className="mt-1 text-xl font-black text-[#0A2540]">{title}</h2><p className="mt-1 text-sm font-semibold text-[#475569]">{detail}</p></div>
          <button className="payment-icon-button" type="button" aria-label={`Close ${title}`} onClick={onClose}><X className="size-4" aria-hidden="true" /></button>
        </header>
        <div className="max-h-[calc(100vh-10rem)] overflow-y-auto p-5">{children}</div>
      </section>
    </div>
  );
}

function PaymentSectionHeading({ eyebrow, title, detail }: { eyebrow: string; title: string; detail: string }) {
  return <div><p className="text-[10px] font-black uppercase tracking-[.16em] text-[#14B8A6]">{eyebrow}</p><h3 className="mt-1 text-lg font-black text-[#0A2540]">{title}</h3><p className="mt-1 text-sm font-semibold leading-6 text-[#475569]">{detail}</p></div>;
}

function PaymentDefinition({ label, value }: { label: string; value: string }) {
  return <div className="min-w-0 rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] p-3"><dt className="text-[10px] font-black uppercase tracking-[.13em] text-[#14B8A6]">{label}</dt><dd className="mt-1 break-words text-sm font-black text-[#0A2540]">{value}</dd></div>;
}

function PaymentField({ label, value, onChange, type = "text", required = false }: { label: string; value: string; onChange: (value: string) => void; type?: string; required?: boolean }) {
  return <label className="payment-label">{label}<input className="payment-input" type={type} required={required} min={type === "number" ? "0.01" : undefined} step={type === "number" ? "0.01" : undefined} value={value} onChange={(event) => onChange(event.target.value)} /></label>;
}

function PaymentStatus({ value, warning = false }: { value: string; warning?: boolean }) {
  return <span className={`inline-flex min-h-6 items-center rounded-full border px-2 text-[9px] font-black uppercase tracking-[.1em] ${warning ? "border-[#FDBA74] bg-[#FFF7ED] text-[#9A3412]" : "border-[#99F6E4] bg-[#F0FDFA] text-[#0F766E]"}`}>{value.replaceAll("_", " ")}</span>;
}

function PaymentNotice({ tone, onClose, children }: { tone: "success" | "error"; onClose: () => void; children: ReactNode }) {
  return <div className={`flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-sm font-bold ${tone === "success" ? "border-[#99F6E4] bg-[#F0FDFA] text-[#115E59]" : "border-[#FECACA] bg-[#FEF2F2] text-[#991B1B]"}`} role={tone === "error" ? "alert" : "status"}><span>{children}</span><button type="button" aria-label="Dismiss message" onClick={onClose}><X className="size-4" aria-hidden="true" /></button></div>;
}

function PaymentEmpty({ icon: Icon, title, detail }: { icon: typeof Banknote; title: string; detail: string }) {
  return <div className="mx-auto grid max-w-md justify-items-center gap-2 px-5 py-10 text-center"><span className="grid size-11 place-items-center rounded-2xl border border-[#BFEAFF] bg-[#F4FBFF] text-[#14B8A6]"><Icon className="size-5" aria-hidden="true" /></span><p className="font-black text-[#0A2540]">{title}</p><p className="text-sm font-semibold leading-6 text-[#64748B]">{detail}</p></div>;
}

function PaymentLoading({ label }: { label: string }) {
  return <div className="flex items-center justify-center gap-2 p-8 text-sm font-bold text-[#475569]"><LoaderCircle className="size-5 animate-spin text-[#14B8A6]" aria-hidden="true" />{label}</div>;
}

function money(cents: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((Number(cents) || 0) / 100);
}

function formatDate(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric" }).format(date);
}

function formatDateTime(value: string) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : new Intl.DateTimeFormat("en-US", { dateStyle: "medium", timeStyle: "short" }).format(date);
}

function formatBytes(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const PAYMENT_CENTER_STYLE = `
  .payment-center-enterprise { --payment-ink: #0A2540; --payment-aqua: #14B8A6; --payment-line: #BFEAFF; }
  .payment-center-enterprise *, .payment-center-enterprise *::before, .payment-center-enterprise *::after { box-sizing: border-box; min-width: 0; }
  .payment-button { display: inline-flex; min-height: 2.75rem; align-items: center; justify-content: center; gap: .5rem; border-radius: 999px; border: 1px solid var(--payment-line); padding: .65rem 1rem; font-size: .75rem; font-weight: 900; transition: 160ms ease; }
  .payment-button:disabled { cursor: not-allowed; opacity: .45; }
  .payment-button-primary { border-color: var(--payment-aqua); background: var(--payment-aqua); color: white; }
  .payment-button-primary:hover:not(:disabled) { background: #0F9F91; }
  .payment-button-secondary { background: white; color: var(--payment-ink); }
  .payment-button-secondary:hover:not(:disabled) { border-color: var(--payment-aqua); background: #F0FDFA; }
  .payment-button-danger { border-color: #FECACA; background: #FEF2F2; color: #991B1B; }
  .payment-button-danger:hover:not(:disabled) { background: #FEE2E2; }
  .payment-icon-button, .payment-page-button { display: inline-grid; width: 2.5rem; height: 2.5rem; place-items: center; border: 1px solid var(--payment-line); border-radius: .8rem; background: white; color: var(--payment-ink); }
  .payment-icon-button:hover:not(:disabled), .payment-page-button:hover:not(:disabled) { border-color: var(--payment-aqua); color: var(--payment-aqua); }
  .payment-icon-button:disabled, .payment-page-button:disabled { opacity: .4; }
  .payment-input { display: block; width: 100%; max-width: 100%; min-width: 0; min-height: 2.75rem; border: 1px solid var(--payment-line); border-radius: .75rem; background: white; padding: .65rem .8rem; color: var(--payment-ink); font-size: .8rem; font-weight: 700; outline: none; }
  .payment-input:focus { border-color: var(--payment-aqua); box-shadow: 0 0 0 3px rgba(20,184,166,.14); }
  .payment-label { display: grid; gap: .4rem; color: var(--payment-ink); font-size: .68rem; font-weight: 900; letter-spacing: .08em; text-transform: uppercase; }
  .payment-row-action { min-height: 2rem; border: 1px solid var(--payment-line); border-radius: .55rem; background: white; padding: .35rem .55rem; font-size: .65rem; font-weight: 900; color: var(--payment-ink); }
  .payment-row-action:disabled { opacity: .4; }
  @media (max-width: 640px) { .payment-button { width: 100%; } }
`;
